/**
 * StoryCameraCapture — full-screen in-app camera for Stories (Phase 1).
 *
 * Open the user's camera (front or back), let them capture a photo or record
 * a 30s-max video, then hand the resulting File back to the parent so it can
 * open StoryEditor for text/sticker overlays.
 *
 * Phase 3 also lives here: live colour filters (CSS `filter`) applied to the
 * preview AND baked into the final photo. 10 presets covering the classic
 * Instagram / Snapchat colour-grade range (B&W, sepia, vintage, vivid, cool,
 * warm, faded, drama, neon, hyperreal). MediaPipe face-AR is a future
 * Phase 3b add-on — the filter strip below is already laid out to host it.
 *
 * Works on Web, iOS Capacitor (NSCameraUsageDescription set), and Android
 * Capacitor (CAMERA + RECORD_AUDIO permissions set).
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Camera, RefreshCcw, Circle, Square, Sparkles, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { useFaceLandmarker, FACE_FILTERS } from './faceFilters';
import { applyPixelFilter } from './pixelFilters';

const MAX_VIDEO_SECONDS = 60;

// Live colour filters — applied to the <video> via CSS filter and baked into
// the canvas at photo-capture time so the filter is preserved on the saved
// story. Each preset has a label and a CSS filter string.
const FILTERS = [
  { id: 'none',     label: 'Original', css: 'none' },
  { id: 'noir',     label: 'Noir',     css: 'grayscale(1) contrast(1.1)' },
  { id: 'sepia',    label: 'Sépia',    css: 'sepia(0.8) contrast(1.05) brightness(1.05)' },
  { id: 'vintage',  label: 'Vintage',  css: 'sepia(0.35) contrast(1.1) saturate(1.2) hue-rotate(-10deg)' },
  { id: 'vivid',    label: 'Vivid',    css: 'saturate(1.8) contrast(1.15)' },
  { id: 'cool',     label: 'Cool',     css: 'hue-rotate(-25deg) saturate(1.1) brightness(1.05)' },
  { id: 'warm',     label: 'Warm',     css: 'hue-rotate(15deg) saturate(1.2) brightness(1.05)' },
  { id: 'faded',    label: 'Faded',    css: 'contrast(0.9) saturate(0.7) brightness(1.1)' },
  { id: 'drama',    label: 'Drama',    css: 'contrast(1.4) brightness(0.95) saturate(1.1)' },
  { id: 'neon',     label: 'Neon',     css: 'saturate(2.5) hue-rotate(45deg) contrast(1.2)' },
  { id: 'hyperreal',label: 'HyperReal',css: 'saturate(1.5) contrast(1.25) brightness(1.08)' },
];

function pickVideoMime() {
  const candidates = [
    'video/mp4;codecs=h264,aac',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  for (const c of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) return c;
  }
  return 'video/webm';
}

const StoryCameraCapture = ({ onCancel, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  // Canvas pipeline used to bake the colour filter into recorded videos.
  // We draw each frame of the camera <video> onto this canvas with
  // ctx.filter = activeFilter.css, then captureStream(30) to feed MediaRecorder.
  const filterCanvasRef = useRef(null);
  const filterRafRef = useRef(0);
  const filterCssRef = useRef('none');

  const [facing, setFacing] = useState('user');
  const [mode, setMode] = useState('photo');
  const [recording, setRecording] = useState(false);
  const [denied, setDenied] = useState(false);
  const [filterId, setFilterId] = useState('none');
  const [faceFilterId, setFaceFilterId] = useState('none');
  const [stripTab, setStripTab] = useState('color'); // 'color' | 'face'

  const activeFilter = FILTERS.find(f => f.id === filterId) || FILTERS[0];
  const activeFaceFilter = FACE_FILTERS.find(f => f.id === faceFilterId) || FACE_FILTERS[0];
  // Pre-warm the MediaPipe model as soon as the user opens the Visage tab so
  // the first filter pick feels instant (model is 2.4 MB — cached after first
  // load, so subsequent opens of the camera are instant).
  const faceEnabled = faceFilterId !== 'none' || stripTab === 'face';
  const { ready: faceReady, detect: detectFace } = useFaceLandmarker(faceEnabled);
  const faceFilterIdRef = useRef('none');
  const facingRef = useRef('user');
  // Live AR overlay canvas drawn over the camera <video> preview.
  const arCanvasRef = useRef(null);
  const arRafRef = useRef(0);
  // Keep a ref-mirror of the CSS so the RAF draw loop sees the latest filter
  // without re-binding (mutating refs in render breaks the React 19 purity rule,
  // so we sync it via useEffect instead).
  useEffect(() => {
    filterCssRef.current = activeFilter.css;
  }, [activeFilter.css]);
  // Keep ref-mirrors so the RAF draw loop (created once per recording)
  // always reads the latest filter ids without re-binding.
  const filterIdRef = useRef('none');
  useEffect(() => { filterIdRef.current = filterId; }, [filterId]);
  useEffect(() => { faceFilterIdRef.current = faceFilterId; }, [faceFilterId]);
  useEffect(() => { facingRef.current = facing; }, [facing]);

  // Live AR overlay loop on the preview — only runs when a face filter is on.
  useEffect(() => {
    if (faceFilterId === 'none' || denied || !faceReady) return;
    const video = videoRef.current;
    const canvas = arCanvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');
    let lastDetectTs = 0;
    let lastLandmarks = null;
    let running = true;

    const loop = () => {
      if (!running) return;
      arRafRef.current = requestAnimationFrame(loop);
      if (!video.videoWidth) return;
      if (canvas.width !== video.videoWidth) canvas.width = video.videoWidth;
      if (canvas.height !== video.videoHeight) canvas.height = video.videoHeight;
      const now = performance.now();
      // Run detection at ~20 fps to keep CPU cost low.
      if (now - lastDetectTs >= 50) {
        lastDetectTs = now;
        const fresh = detectFace(video, Math.round(now));
        if (fresh) lastLandmarks = fresh;
      }
      // Redraw every frame using the most recent landmarks so the overlay
      // stays visible between detection ticks (fixes the flicker where the
      // sticker vanished at 60fps clearRect but only redrawn at 20fps).
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (!lastLandmarks) return;
      const def = FACE_FILTERS.find(f => f.id === faceFilterIdRef.current);
      if (!def || !def.draw) return;
      if (facingRef.current === 'user') {
        ctx.save();
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        def.draw(ctx, lastLandmarks, canvas.width, canvas.height);
        ctx.restore();
      } else {
        def.draw(ctx, lastLandmarks, canvas.width, canvas.height);
      }
    };
    arRafRef.current = requestAnimationFrame(loop);
    return () => {
      running = false;
      cancelAnimationFrame(arRafRef.current);
    };
  }, [faceFilterId, denied, faceReady, detectFace]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // (Re)start camera stream when facing/mode changes.
  useEffect(() => {
    let cancelled = false;

    function stopExisting() {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    }

    async function start() {
      try {
        stopExisting();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1080 },
            height: { ideal: 1920 },
          },
          audio: mode === 'video',
        });
        if (cancelled) {
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        setDenied(false);
      } catch (err) {
        console.error('[StoryCameraCapture] getUserMedia failed:', err);
        setDenied(true);
      }
    }

    start();

    return () => {
      cancelled = true;
    };
  }, [facing, mode]);

  // Final tear-down on unmount.
  useEffect(() => {
    return () => {
      if (recRef.current && recRef.current.state !== 'inactive') {
        try { recRef.current.stop(); } catch (_e) { /* ignore */ }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Auto-stop the recording after MAX_VIDEO_SECONDS.
  useEffect(() => {
    if (!recording) return;
    const id = setTimeout(() => {
      if (recRef.current && recRef.current.state !== 'inactive') {
        try { recRef.current.stop(); } catch (_e) { /* ignore */ }
      }
      setRecording(false);
    }, MAX_VIDEO_SECONDS * 1000);
    return () => clearTimeout(id);
  }, [recording]);

  function capturePhoto() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Draw the raw frame (mirrored on selfies).
    if (facing === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    // Bake the colour filter via direct pixel manipulation — works on
    // every browser including iOS WebKit (where ctx.filter is a no-op).
    if (filterId !== 'none') {
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      applyPixelFilter(imgData.data, filterId);
      ctx.putImageData(imgData, 0, 0);
    }
    // Bake the AR face filter on top (face landmarks detected on the still
    // frame so the overlay matches what the user saw on screen).
    if (faceFilterId !== 'none' && activeFaceFilter.draw) {
      const lm = detectFace(video, performance.now());
      if (lm) {
        if (facing === 'user') {
          ctx.save();
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
          activeFaceFilter.draw(ctx, lm, canvas.width, canvas.height);
          ctx.restore();
        } else {
          activeFaceFilter.draw(ctx, lm, canvas.width, canvas.height);
        }
      }
    }
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Échec de la capture'); return; }
      const file = new File([blob], `story-${Date.now()}.jpg`, { type: 'image/jpeg' });
      onCapture(file, 'image');
    }, 'image/jpeg', 0.92);
  }

  function startRecording() {
    if (!streamRef.current) return;
    const mime = pickVideoMime();
    try {
      // Cap recording canvas at 720p to keep the pixel-filter loop fast on
      // mid-range phones (a 1080x1920 frame is 3.8 ms slower per RAF tick).
      const video = videoRef.current;
      const vw = video?.videoWidth || 720;
      const vh = video?.videoHeight || 1280;
      const scale = Math.min(1, 720 / Math.min(vw, vh));
      const w = Math.round(vw * scale);
      const h = Math.round(vh * scale);
      let canvas = filterCanvasRef.current;
      if (!canvas) {
        canvas = document.createElement('canvas');
        filterCanvasRef.current = canvas;
      }
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      const draw = () => {
        if (!recRef.current || recRef.current.state === 'inactive') return;
        if (video && video.readyState >= 2) {
          ctx.save();
          if (facingRef.current === 'user') {
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
          }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          ctx.restore();
          // Bake the colour filter via pixel manipulation (works on iOS).
          const fId = filterIdRef.current;
          if (fId && fId !== 'none') {
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            applyPixelFilter(img.data, fId);
            ctx.putImageData(img, 0, 0);
          }
          // Bake AR face filter on top.
          const ffid = faceFilterIdRef.current;
          if (ffid && ffid !== 'none') {
            const def = FACE_FILTERS.find(f => f.id === ffid);
            if (def && def.draw) {
              const lm = detectFace(video, performance.now());
              if (lm) {
                if (facingRef.current === 'user') {
                  ctx.save();
                  ctx.translate(canvas.width, 0);
                  ctx.scale(-1, 1);
                  def.draw(ctx, lm, canvas.width, canvas.height);
                  ctx.restore();
                } else {
                  def.draw(ctx, lm, canvas.width, canvas.height);
                }
              }
            }
          }
        }
        filterRafRef.current = requestAnimationFrame(draw);
      };

      const canvasStream = canvas.captureStream(30);
      // Add the original audio tracks (mic) to the canvas-derived stream so
      // the recorded video keeps sound.
      streamRef.current.getAudioTracks().forEach(t => canvasStream.addTrack(t));

      const rec = new MediaRecorder(canvasStream, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        cancelAnimationFrame(filterRafRef.current);
        const blob = new Blob(chunksRef.current, { type: mime });
        const ext = mime.includes('mp4') ? 'mp4' : 'webm';
        const file = new File([blob], `story-${Date.now()}.${ext}`, { type: mime });
        onCapture(file, 'video');
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      // Kick off the drawing loop now that recRef.current is set.
      filterRafRef.current = requestAnimationFrame(draw);
    } catch (err) {
      console.error('[StoryCameraCapture] MediaRecorder failed:', err);
      toast.error('Enregistrement vidéo non supporté sur cet appareil');
    }
  }

  function stopRecording() {
    if (filterRafRef.current) {
      cancelAnimationFrame(filterRafRef.current);
      filterRafRef.current = 0;
    }
    if (recRef.current && recRef.current.state !== 'inactive') {
      try { recRef.current.stop(); } catch (_e) { /* already stopped */ }
    }
    setRecording(false);
  }

  return ReactDOM.createPortal((
    <div className="fixed inset-0 z-[2147483647] bg-black flex flex-col" data-testid="story-camera-overlay">
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <button
          onClick={onCancel}
          className="p-2 -ml-2 rounded-full bg-black/40 hover:bg-black/60"
          data-testid="story-camera-close"
          aria-label="Fermer la caméra"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        {recording ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/90" data-testid="story-camera-rec-indicator">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            <span className="text-xs font-bold text-white">REC</span>
          </div>
        ) : (
          <span className="text-white/80 text-xs font-medium">Caméra</span>
        )}

        <div className="w-10" />
      </div>

      {/* Video preview */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center">
        {denied ? (
          <div className="text-center px-6 max-w-sm">
            <Camera className="w-12 h-12 text-white/40 mx-auto mb-3" />
            <p className="text-white/80 font-semibold mb-2">Accès caméra refusé</p>
            <p className="text-white/60 text-sm">
              Active la caméra et le microphone dans les Réglages de votre appareil pour Destine.
            </p>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{
                transform: facing === 'user' ? 'scaleX(-1)' : undefined,
                filter: activeFilter.css,
              }}
              data-testid="story-camera-preview"
            />
            {/* AR overlay canvas (face filters) — drawn on top of the live video. */}
            {faceFilterId !== 'none' && (
              <canvas
                ref={arCanvasRef}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                data-testid="story-camera-ar-overlay"
              />
            )}
            {recording && (
              <div className="absolute top-0 left-0 right-0 h-1 bg-white/15">
                <div
                  className="h-full bg-red-500 origin-left"
                  style={{ animation: `storyCamProgress ${MAX_VIDEO_SECONDS}s linear forwards`, width: '100%' }}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom controls */}
      <div
        className="px-4 pt-3 pb-6 bg-gradient-to-t from-black via-black/70 to-transparent"
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        {/* Filter strip — Color OR Face AR tab */}
        {!recording && (
          <>
            <div className="flex items-center justify-center gap-2 mb-2">
              <button
                onClick={() => setStripTab('color')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${stripTab === 'color' ? 'bg-white text-black' : 'bg-white/10 text-white/70'}`}
                data-testid="story-camera-tab-color"
              >
                <Palette className="w-3.5 h-3.5" /> Couleur
              </button>
              <button
                onClick={() => setStripTab('face')}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${stripTab === 'face' ? 'bg-white text-black' : 'bg-white/10 text-white/70'}`}
                data-testid="story-camera-tab-face"
              >
                <Sparkles className="w-3.5 h-3.5" /> Visage
              </button>
            </div>

            {stripTab === 'color' ? (
              <div className="flex gap-2.5 overflow-x-auto pb-3 no-scrollbar" data-testid="story-camera-filter-strip">
                {FILTERS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFilterId(f.id)}
                    className={`shrink-0 flex flex-col items-center gap-1 transition-all ${filterId === f.id ? 'scale-105' : 'opacity-80'}`}
                    data-testid={`story-camera-filter-${f.id}`}
                  >
                    <div
                      className={`w-12 h-12 rounded-full overflow-hidden border-2 ${filterId === f.id ? 'border-white' : 'border-white/30'}`}
                      style={{
                        background: 'linear-gradient(135deg, #1a0023 0%, #450050 50%, #e753fe 100%)',
                        filter: f.css,
                      }}
                      aria-hidden="true"
                    />
                    <span className={`text-[10px] font-medium tracking-wide ${filterId === f.id ? 'text-white' : 'text-white/60'}`}>
                      {f.label}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex gap-2.5 overflow-x-auto pb-3 no-scrollbar" data-testid="story-camera-face-strip">
                {FACE_FILTERS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFaceFilterId(f.id)}
                    className={`shrink-0 flex flex-col items-center gap-1 transition-all ${faceFilterId === f.id ? 'scale-105' : 'opacity-80'}`}
                    data-testid={`story-camera-face-${f.id}`}
                  >
                    <div className={`w-12 h-12 rounded-full overflow-hidden border-2 flex items-center justify-center text-2xl ${faceFilterId === f.id ? 'border-white bg-white/10' : 'border-white/30 bg-white/5'}`} aria-hidden="true">
                      {f.id === 'none' ? '∅'
                        : f.id === 'sunglasses' ? '🕶️'
                        : f.id === 'crown' ? '👑'
                        : f.id === 'hearts' ? '💖'
                        : f.id === 'dog' ? '🐶'
                        : f.id === 'cat' ? '🐱'
                        : f.id === 'mustache' ? '〰️'
                        : f.id === 'mask' ? '🎭' : '•'}
                    </div>
                    <span className={`text-[10px] font-medium tracking-wide ${faceFilterId === f.id ? 'text-white' : 'text-white/60'}`}>
                      {f.label}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {faceFilterId !== 'none' && !faceReady && (
              <p className="text-center text-[10px] text-white/60 mb-2">Chargement du modèle AR…</p>
            )}
          </>
        )}

        {!recording && (
          <div className="flex items-center justify-center gap-6 mb-4">
            <button
              onClick={() => setMode('photo')}
              className={`text-sm font-semibold tracking-wide uppercase transition-colors ${mode === 'photo' ? 'text-white' : 'text-white/50'}`}
              data-testid="story-camera-mode-photo"
            >
              Photo
            </button>
            <button
              onClick={() => setMode('video')}
              className={`text-sm font-semibold tracking-wide uppercase transition-colors ${mode === 'video' ? 'text-white' : 'text-white/50'}`}
              data-testid="story-camera-mode-video"
            >
              Vidéo
            </button>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="w-12" />

          {mode === 'photo' ? (
            <button
              onClick={capturePhoto}
              disabled={denied}
              className="relative w-20 h-20 rounded-full border-4 border-white bg-white/10 disabled:opacity-30 active:scale-95 transition-transform"
              data-testid="story-camera-shutter-photo"
              aria-label="Prendre une photo"
            >
              <Circle className="w-12 h-12 text-white absolute inset-0 m-auto" fill="white" />
            </button>
          ) : !recording ? (
            <button
              onClick={startRecording}
              disabled={denied}
              className="w-20 h-20 rounded-full border-4 border-white bg-red-600/80 disabled:opacity-30 active:scale-95 transition-transform"
              data-testid="story-camera-shutter-record"
              aria-label="Démarrer l'enregistrement"
            />
          ) : (
            <button
              onClick={stopRecording}
              className="w-20 h-20 rounded-full border-4 border-white bg-red-600 flex items-center justify-center active:scale-95 transition-transform"
              data-testid="story-camera-shutter-stop"
              aria-label="Arrêter l'enregistrement"
            >
              <Square className="w-7 h-7 text-white" fill="white" />
            </button>
          )}

          {!recording ? (
            <button
              onClick={() => setFacing(p => p === 'user' ? 'environment' : 'user')}
              disabled={denied}
              className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30"
              data-testid="story-camera-flip"
              aria-label="Changer de caméra"
            >
              <RefreshCcw className="w-6 h-6 text-white" />
            </button>
          ) : (
            <div className="w-12" />
          )}
        </div>
      </div>
    </div>
  ), document.body);
};

export default StoryCameraCapture;
